// RD-AUD-013 — operations catalog money-blindness (client wiring).
//
// Base `services` / `chemicals` SELECT no longer includes operations
// (20260901150000). Ops reads dollar-free views (no price / unit_price).
// Every other role keeps the base tables so Log Job can stamp unit_price.
//
// RD-ROLE-005 — worker/supervisor price-blindness. When the tenant setting
// hides prices from a field role (canViewPrices() → false), that role reads
// the dollar-free `field_services` / `field_chemicals` views instead of the
// base tables — mirroring operations' pattern above, but toggleable per
// tenant rather than hardcoded to one role. Admin/billing always get the
// base tables (canSeePrices defaults true and is never read for them).
//
// Pass the AuthContext `role` string (React state), not a stale store read,
// so CatalogProvider re-fetches when role resolves after JWT.

export function catalogTablesForRole(role, canSeePrices = true) {
  if (role === 'operations') {
    return { services: 'operations_services', chemicals: 'operations_chemicals' };
  }
  if ((role === 'worker' || role === 'supervisor') && !canSeePrices) {
    return { services: 'field_services', chemicals: 'field_chemicals' };
  }
  return { services: 'services', chemicals: 'chemicals' };
}

/**
 * @param {{ from: Function }} client
 * @param {string} tenantId
 * @param {string|null|undefined} role
 * @param {boolean} [canSeePrices=true]
 * @returns {Promise<{ services: object[], chemicals: object[] }>}
 */
export async function fetchTenantCatalog(client, tenantId, role, canSeePrices = true) {
  const tables = catalogTablesForRole(role, canSeePrices);
  const [s, c] = await Promise.all([
    client.from(tables.services).select('*').eq('tenant_id', tenantId).order('name'),
    client.from(tables.chemicals).select('*').eq('tenant_id', tenantId).order('name'),
  ]);
  if (s.error) throw s.error;
  if (c.error) throw c.error;
  return { services: s.data || [], chemicals: c.data || [] };
}

/** Longest a context-held catalog may be reused by a dashboard's first load. */
export const CATALOG_REUSE_MAX_AGE_MS = 30000;

/**
 * RD-PERF-004 — may the dashboard's INITIAL load reuse the catalog CatalogContext
 * already loaded? Only when it is for the same tenant, read the exact same table
 * pair this caller would read (so a price-bearing catalog never reaches a session
 * that would have read the price-less views, or vice versa), and is fresh.
 * Also the same user and the same session `aal` as `session` (read fresh by the caller).
 * `canSeePrices` defaults true like fetchTenantCatalog. Anything doubtful -> false.
 */
export function canReuseLoadedCatalog(loaded, tenantId, role, canSeePrices = true, now = Date.now(), session = null) {
  if (!loaded || !tenantId || loaded.tenantId !== tenantId) return false;
  // A pre-2FA (aal1) read returns EMPTY under RLS; it must never seed an aal2 session.
  // Same user AND same assurance level as the CURRENT session, or fetch (fail closed).
  if (!loaded.userId || !loaded.aal || !session?.userId || !session?.aal) return false;
  if (loaded.userId !== session.userId || loaded.aal !== session.aal) return false;
  const t = catalogTablesForRole(role, canSeePrices);
  if (loaded.tablesKey !== `${t.services}|${t.chemicals}`) return false;
  return now - loaded.at <= CATALOG_REUSE_MAX_AGE_MS;
}
